import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getMasterEquipmentDefinitions,
  updateTenantEquipmentSchema,
  getTenantInfo,
  reloadSchemaCache
} from '../services/api';
import {
  Box,
  Flex,
  Button,
  Text,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertDescription,
  IconButton,
  VStack,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  useDisclosure,
  useToast,
  Input,
  FormControl,
  FormLabel,
  Select,
  Switch,
  Checkbox,
  Textarea,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Spinner,
  Grid,
  GridItem,
  Divider,
  Badge,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, EditIcon, ChevronLeftIcon, ChevronUpIcon, ChevronDownIcon } from '@chakra-ui/icons';

// 設備タイプのプリセット定義
const EQUIPMENT_TYPE_PRESETS = {
  floorplan: {
    name: '間取り',
    icon: '🏠',
    hover_text: '間取りを選択',
    enabled: true,
    enable_popularity: true,
    enable_guide: true
  },
  kitchen: {
    name: 'キッチン',
    icon: '🍳',
    hover_text: 'キッチンを選択',
    enabled: true,
    enable_popularity: true,
    enable_guide: true
  },
  toilet: {
    name: 'トイレ',
    icon: '🚽',
    hover_text: 'トイレを選択',
    enabled: true,
    enable_popularity: true,
    enable_guide: true
  },
  bath: {
    name: 'お風呂',
    icon: '🛁',
    hover_text: 'お風呂を選択',
    enabled: true,
    enable_popularity: true,
    enable_guide: true
  },
  basin: {
    name: '洗面台',
    icon: '🚿',
    hover_text: '洗面台を選択',
    enabled: true,
    enable_popularity: true,
    enable_guide: true
  }
};

// 設備タイプ編集モーダル
function EquipmentTypeModal({ isOpen, onClose, equipmentType, onSave, isNew = false, existingTypeKeys = [] }) {
  const [formData, setFormData] = useState({
    name: '',
    icon: '',
    hover_text: '',
    enabled: true,
    enable_popularity: true,
    enable_guide: true,
    fields: {}
  });
  const [selectedPreset, setSelectedPreset] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [customKey, setCustomKey] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (isNew) {
        setFormData({
          name: '',
          icon: '',
          hover_text: '',
          enabled: true,
          enable_popularity: true,
          enable_guide: true,
          fields: {}
        });
        setSelectedPreset('');
        setIsCustom(false);
        setCustomKey('');
      } else if (equipmentType) {
        setFormData({
          name: equipmentType.name || '',
          icon: equipmentType.icon || '',
          hover_text: equipmentType.hover_text || '',
          enabled: equipmentType.enabled !== false,
          enable_popularity: equipmentType.enable_popularity !== false,
          enable_guide: equipmentType.enable_guide !== false,
          fields: equipmentType.fields || {}
        });
        setSelectedPreset('');
        setIsCustom(true);
      }
    }
  }, [isOpen, equipmentType, isNew]);

  // プリセット選択時の処理
  const handlePresetChange = (presetKey) => {
    setSelectedPreset(presetKey);
    if (presetKey === 'custom') {
      setIsCustom(true);
      setFormData(prev => ({ ...prev, name: '', icon: '', hover_text: '', enable_popularity: true, enable_guide: true }));
    } else if (presetKey && EQUIPMENT_TYPE_PRESETS[presetKey]) {
      setIsCustom(false);
      const preset = EQUIPMENT_TYPE_PRESETS[presetKey];
      setFormData(prev => ({
        ...prev,
        name: preset.name,
        icon: preset.icon,
        hover_text: preset.hover_text,
        enabled: preset.enabled,
        enable_popularity: preset.enable_popularity,
        enable_guide: preset.enable_guide
      }));
    }
  };

  // カスタムキー自動生成
  const generateCustomKey = (name) => {
    let baseKey = name.toLowerCase().replace(/[^a-z0-9]/g, '_');
    let key = baseKey;
    let counter = 1;
    
    while (existingTypeKeys.includes(key)) {
      key = `${baseKey}_${counter}`;
      counter++;
    }
    
    return key;
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      alert('設備タイプ名を入力してください');
      return;
    }
    
    setLoading(true);
    try {
      let finalKey;
      if (isNew) {
        if (selectedPreset && selectedPreset !== 'custom') {
          finalKey = selectedPreset;
        } else {
          finalKey = customKey || generateCustomKey(formData.name);
        }
      }
      
      await onSave(formData, finalKey);
      onClose();
    } catch (error) {
      console.error('Error saving equipment type:', error);
      alert('保存に失敗しました: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {isNew ? '新規設備タイプ' : '設備タイプ編集'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            {isNew && (
              <FormControl>
                <FormLabel>設備タイプを選択</FormLabel>
                <Select 
                  value={selectedPreset} 
                  onChange={(e) => handlePresetChange(e.target.value)}
                  placeholder="設備タイプを選択してください"
                >
                  {Object.entries(EQUIPMENT_TYPE_PRESETS).map(([key, preset]) => (
                    <option 
                      key={key} 
                      value={key} 
                      disabled={existingTypeKeys.includes(key)}
                    >
                      {preset.icon} {preset.name} {existingTypeKeys.includes(key) ? '(既に存在)' : ''}
                    </option>
                  ))}
                  <option value="custom">🔧 その他（カスタム）</option>
                </Select>
              </FormControl>
            )}

            {(isCustom && isNew) && (
              <FormControl>
                <FormLabel>変数名（英数字・アンダースコア）</FormLabel>
                <Input
                  value={customKey}
                  onChange={(e) => setCustomKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  placeholder="例: custom_equipment"
                />
                <Text fontSize="sm" color="gray.500" mt={1}>
                  空白の場合、設備タイプ名から自動生成されます
                </Text>
              </FormControl>
            )}

            <FormControl>
              <FormLabel>設備タイプ名</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="例: キッチン"
                disabled={!isNew && !isCustom}
              />
            </FormControl>

            <FormControl>
              <FormLabel>アイコン</FormLabel>
              <Input
                value={formData.icon}
                onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
                placeholder="例: 🏠"
                disabled={!isNew && !isCustom}
              />
            </FormControl>

            <FormControl>
              <FormLabel>マウスオーバー表示名</FormLabel>
              <Input
                value={formData.hover_text}
                onChange={(e) => setFormData(prev => ({ ...prev, hover_text: e.target.value }))}
                placeholder="例: キッチンを選択"
                disabled={!isNew && !isCustom}
              />
            </FormControl>

            <FormControl>
              <FormLabel>有効化</FormLabel>
              <Switch
                isChecked={formData.enabled}
                onChange={(e) => setFormData(prev => ({ ...prev, enabled: e.target.checked }))}
              />
            </FormControl>

            <FormControl>
              <FormLabel>人気順表示</FormLabel>
              <Switch
                isChecked={formData.enable_popularity !== false}
                onChange={(e) => setFormData(prev => ({ ...prev, enable_popularity: e.target.checked }))}
              />
              <Text fontSize="sm" color="gray.500" mt={1}>
                無効にすると1位-3位の装飾がなくなり、人気順ソートが使用できなくなります
              </Text>
            </FormControl>

            <FormControl>
              <FormLabel>設備選び方ガイド表示</FormLabel>
              <Switch
                isChecked={formData.enable_guide !== false}
                onChange={(e) => setFormData(prev => ({ ...prev, enable_guide: e.target.checked }))}
              />
              <Text fontSize="sm" color="gray.500" mt={1}>
                無効にすると設備選び方ガイドのタブが表示されなくなります
              </Text>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button mr={3} onClick={onClose}>
            キャンセル
          </Button>
          <Button colorScheme="blackAlpha" onClick={handleSubmit} isLoading={loading}>
            保存
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// フィールド編集モーダル
function FieldModal({ isOpen, onClose, field, fieldName, onSave, isNew = false }) {
  const [formData, setFormData] = useState({
    name: '',
    label: '',
    type: 'string',
    required: false,
    unit: '',
    options: [],
    showInCard: true,
    items: {
      type: 'string'
    }
  });
  const [optionText, setOptionText] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (isNew) {
        setFormData({
          name: '',
          label: '',
          type: 'string',
          required: false,
          unit: '',
          options: [],
          showInCard: true,
          items: {
            type: 'string'
          }
        });
      } else if (field) {
        setFormData({
          name: fieldName || '',
          label: field.label || '',
          type: field.type || 'string',
          required: field.required || false,
          unit: field.unit || '',
          options: field.options || [],
          showInCard: field.showInCard !== undefined ? field.showInCard : true,
          items: field.items || {
            type: 'string'
          }
        });
        setOptionText((field.options || []).join('\n'));
      }
    }
  }, [isOpen, field, fieldName, isNew]);

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      alert('フィールド名を入力してください');
      return;
    }
    
    if (!formData.label.trim()) {
      alert('フィールドラベルを入力してください');
      return;
    }

    setLoading(true);
    try {
      const finalData = { ...formData };
      
      // selectタイプの場合はoptionsを設定
      if (formData.type === 'select') {
        finalData.options = optionText.split('\n').filter(option => option.trim());
        delete finalData.items; // select型の場合はitemsを削除
      } else {
        delete finalData.options; // select型以外はoptionsを削除
      }
      
      // array型以外の場合はitemsを削除
      if (formData.type !== 'array') {
        delete finalData.items;
      }
      
      await onSave(formData.name, finalData);
      onClose();
    } catch (error) {
      console.error('Error saving field:', error);
      alert('保存に失敗しました: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {isNew ? '新規フィールド' : 'フィールド編集'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl>
              <FormLabel>フィールド名 (内部名)</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="例: price"
                isDisabled={!isNew}
              />
            </FormControl>

            <FormControl>
              <FormLabel>表示名</FormLabel>
              <Input
                value={formData.label}
                onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
                placeholder="例: 価格"
              />
            </FormControl>

            <FormControl>
              <FormLabel>データタイプ</FormLabel>
              <Select
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
              >
                <option value="string">文字列</option>
                <option value="number">数値</option>
                <option value="boolean">真偽値</option>
                <option value="select">選択肢</option>
              </Select>
            </FormControl>

            {formData.type === 'select' && (
              <FormControl>
                <FormLabel>選択肢 (1行に1つ)</FormLabel>
                <Textarea
                  value={optionText}
                  onChange={(e) => setOptionText(e.target.value)}
                  placeholder="オプション1&#10;オプション2&#10;オプション3"
                  rows={4}
                />
              </FormControl>
            )}

            <FormControl>
              <FormLabel>単位</FormLabel>
              <Input
                value={formData.unit}
                onChange={(e) => setFormData(prev => ({ ...prev, unit: e.target.value }))}
                placeholder="例: 円, m², 個"
              />
            </FormControl>

            <FormControl>
              <FormLabel>必須項目</FormLabel>
              <Switch
                isChecked={formData.required}
                onChange={(e) => setFormData(prev => ({ ...prev, required: e.target.checked }))}
              />
            </FormControl>

            <FormControl>
              <FormLabel>カード表示</FormLabel>
              <Switch
                isChecked={formData.showInCard}
                onChange={(e) => setFormData(prev => ({ ...prev, showInCard: e.target.checked }))}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button mr={3} onClick={onClose}>
            キャンセル
          </Button>
          <Button colorScheme="blackAlpha" onClick={handleSubmit} isLoading={loading}>
            保存
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default function MasterEquipmentDefinitions() {
  const { currentUser } = useAuth();
  const [definitions, setDefinitions] = useState({});
  const [originalDefinitions, setOriginalDefinitions] = useState({}); // 元のデータを保持
  const [hasChanges, setHasChanges] = useState(false); // 変更があるかどうか
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [tenantInfo, setTenantInfo] = useState(null);
  const navigate = useNavigate();
  const toast = useToast();

  // モーダル制御
  const { isOpen: isTypeModalOpen, onOpen: onTypeModalOpen, onClose: onTypeModalClose } = useDisclosure();
  const { isOpen: isFieldModalOpen, onOpen: onFieldModalOpen, onClose: onFieldModalClose } = useDisclosure();
  
  // 編集中のデータ
  const [selectedEquipmentType, setSelectedEquipmentType] = useState(null);
  const [selectedEquipmentTypeKey, setSelectedEquipmentTypeKey] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [selectedFieldName, setSelectedFieldName] = useState(null);
  const [isNewType, setIsNewType] = useState(false);
  const [isNewField, setIsNewField] = useState(false);

  // 編集・削除不可フィールドの定義
  const nonEditableFields = ['images', 'name', 'manufacturer', 'price', 'features', 'recommended'];

  // デフォルトフィールドの定義
  const defaultFields = {
    name: {
      label: '名称',
      type: 'string',
      required: true,
      showInCard: true,
      display_order: 0
    },
    manufacturer: {
      label: 'メーカー',
      type: 'string',
      required: true,
      showInCard: true,
      display_order: 1
    },
    price: {
      label: '価格',
      type: 'number',
      unit: '円',
      required: true,
      showInCard: true,
      display_order: 2
    },
    features: {
      label: '特徴',
      type: 'string',
      required: false,
      showInCard: true,
      display_order: 3
    },
    recommended: {
      label: 'おすすめ',
      type: 'boolean',
      required: false,
      showInCard: true,
      display_order: 4
    },
    default_quantity: {
      label: 'デフォルト数量',
      type: 'number',
      required: false,
      showInCard: false,
      display_order: 5
    },
    images: {
      label: '画像',
      type: 'array',
      items: {
        type: 'string'
      },
      required: false,
      showInCard: false,
      display_order: 6
    }
  };

  // フィールドが編集・削除可能かチェック
  const isFieldEditable = (fieldName) => {
    return !nonEditableFields.includes(fieldName);
  };

  // データ読み込み
  const loadDefinitions = useCallback(async (forceReload = false) => {
    if (!currentUser) return;
    
    // 変更がある場合は確認ダイアログを表示
    if (hasChanges && !forceReload) {
      const confirmReload = window.confirm(
        '未保存の変更があります。データを再読み込みすると変更が失われます。続行しますか？'
      );
      if (!confirmReload) {
        return;
      }
    }
    
    setLoading(true);
    setError(null);
    try {
      console.log('設備定義データを読み込み中...');
      
      const [definitionsData, tenantData] = await Promise.all([
        getMasterEquipmentDefinitions(),
        getTenantInfo()
      ]);
      
      console.log('読み込んだ設備定義データ:', JSON.stringify(definitionsData, null, 2).substring(0, 1000));
      
      setDefinitions(definitionsData);
      setOriginalDefinitions(JSON.parse(JSON.stringify(definitionsData))); // ディープコピー
      setTenantInfo(tenantData);
      setHasChanges(false); // 読み込み完了時は変更なし
      
      console.log('設備定義データの読み込み完了');
    } catch (err) {
      console.error('Error loading definitions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadDefinitions();
  }, [loadDefinitions]);

  // ページ離脱時の警告
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = '未保存の変更があります。ページを離れますか？';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasChanges]);

  // 変更をリセット
  const handleReset = () => {
    if (!hasChanges) return;
    
    if (window.confirm('変更をすべて取り消して元に戻しますか？')) {
      setDefinitions(JSON.parse(JSON.stringify(originalDefinitions)));
      setHasChanges(false);
    }
  };

  // 設備タイプ保存
  const handleSaveEquipmentType = async (typeData, typeKey = null) => {
    if (isNewType) {
      // 新規追加時はデフォルトフィールドを自動追加
      const typeDataWithDefaults = {
        ...typeData,
        display_order: Object.keys(definitions.equipment_types || {}).length,
        fields: {
          ...typeData.fields,
          ...defaultFields
        }
      };
      
      const finalKey = typeKey || typeData.name.toLowerCase().replace(/\s+/g, '_');
      const newDefinitions = {
        ...definitions,
        equipment_types: {
          ...definitions.equipment_types,
          [finalKey]: typeDataWithDefaults
        }
      };
      setDefinitions(newDefinitions);
      setHasChanges(true); // 変更フラグを設定
    } else {
      // 既存更新
      const newDefinitions = {
        ...definitions,
        equipment_types: {
          ...definitions.equipment_types,
          [selectedEquipmentTypeKey]: typeData
        }
      };
      setDefinitions(newDefinitions);
      setHasChanges(true); // 変更フラグを設定
    }
  };

  // フィールド保存
  const handleSaveField = async (fieldName, fieldData) => {
    if (!selectedEquipmentTypeKey) return;
    
    const currentFields = definitions.equipment_types[selectedEquipmentTypeKey].fields || {};
    const isNewField = !currentFields[fieldName];
    
    const updatedFieldData = {
      ...fieldData,
      display_order: isNewField ? Object.keys(currentFields).length : (fieldData.display_order || 0)
    };
    
    const newDefinitions = {
      ...definitions,
      equipment_types: {
        ...definitions.equipment_types,
        [selectedEquipmentTypeKey]: {
          ...definitions.equipment_types[selectedEquipmentTypeKey],
          fields: {
            ...currentFields,
            [fieldName]: updatedFieldData
          }
        }
      }
    };
    setDefinitions(newDefinitions);
    setHasChanges(true); // 変更フラグを設定
  };

  // 設備タイプの表示順変更
  const moveEquipmentType = (typeKey, direction) => {
    const types = Object.entries(definitions.equipment_types || {});
    const currentIndex = types.findIndex(([key]) => key === typeKey);
    
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= types.length) return;
    
    // 配列の要素を入れ替え
    const newTypes = [...types];
    [newTypes[currentIndex], newTypes[newIndex]] = [newTypes[newIndex], newTypes[currentIndex]];
    
    // 新しい順序でオブジェクトを再構築
    const newEquipmentTypes = {};
    newTypes.forEach(([key, value], index) => {
      newEquipmentTypes[key] = {
        ...value,
        display_order: index
      };
    });
    
    setDefinitions({
      ...definitions,
      equipment_types: newEquipmentTypes
    });
    setHasChanges(true); // 変更フラグを設定
  };

  // フィールドの表示順変更
  const moveField = (typeKey, fieldName, direction) => {
    const fields = Object.entries(definitions.equipment_types[typeKey].fields || {});
    const currentIndex = fields.findIndex(([key]) => key === fieldName);
    
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= fields.length) return;
    
    // 配列の要素を入れ替え
    const newFields = [...fields];
    [newFields[currentIndex], newFields[newIndex]] = [newFields[newIndex], newFields[currentIndex]];
    
    // 新しい順序でオブジェクトを再構築
    const newFieldsObj = {};
    newFields.forEach(([key, value], index) => {
      newFieldsObj[key] = {
        ...value,
        display_order: index
      };
    });
    
    setDefinitions({
      ...definitions,
      equipment_types: {
        ...definitions.equipment_types,
        [typeKey]: {
          ...definitions.equipment_types[typeKey],
          fields: newFieldsObj
        }
      }
    });
    setHasChanges(true); // 変更フラグを設定
  };

  // 設備タイプ削除
  const handleDeleteEquipmentType = async (typeKey) => {
    if (!window.confirm('この設備タイプを削除してもよろしいですか？')) return;
    
    const newDefinitions = {
      ...definitions,
      equipment_types: { ...definitions.equipment_types }
    };
    delete newDefinitions.equipment_types[typeKey];
    setDefinitions(newDefinitions);
    setHasChanges(true); // 変更フラグを設定
  };

  // フィールド削除
  const handleDeleteField = async (typeKey, fieldName) => {
    // 編集不可フィールドは削除できない
    if (!isFieldEditable(fieldName)) {
      toast({
        title: '削除できません',
        description: 'このフィールドは編集・削除できません',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!window.confirm('このフィールドを削除してもよろしいですか？')) return;
    
    const newDefinitions = {
      ...definitions,
      equipment_types: {
        ...definitions.equipment_types,
        [typeKey]: {
          ...definitions.equipment_types[typeKey],
          fields: { ...definitions.equipment_types[typeKey].fields }
        }
      }
    };
    delete newDefinitions.equipment_types[typeKey].fields[fieldName];
    setDefinitions(newDefinitions);
    setHasChanges(true); // 変更フラグを設定
  };

  const handleSaveAll = async () => {
    if (!tenantInfo) return;
    
    setSaving(true);
    try {
      console.log('保存前のデータ:', JSON.stringify(definitions, null, 2).substring(0, 500));
      
      // バックエンドではリクエストから自動的にテナントIDが取得されるため、
      // URLパラメータのtenant_idは実際には使用されないが、APIの形式に合わせる
      await updateTenantEquipmentSchema(tenantInfo.tenant_id, definitions);
      
      // 保存成功のログ
      console.log('スキーマ保存成功');
      
      // バックエンドのスキーマキャッシュをリロード（複数回試行）
      let cacheReloadSuccess = false;
      for (let i = 0; i < 3 && !cacheReloadSuccess; i++) {
        try {
          await reloadSchemaCache();
          cacheReloadSuccess = true;
          console.log(`スキーマキャッシュリロード成功 (試行${i + 1}回目)`);
        } catch (cacheError) {
          console.warn(`スキーマキャッシュリロード失敗 (試行${i + 1}回目):`, cacheError);
          if (i < 2) {
            // 少し待ってから再試行
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
      }
      
      // 保存成功後、サーバーから最新データを再読み込み
      await loadDefinitions(true); // forceReload = true
      
      console.log('データ再読み込み完了');
      
      toast({
        title: '設備定義を保存しました',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error saving definitions:', error);
      toast({
        title: '保存に失敗しました',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  // 設備タイプ編集モーダルを開く
  const openTypeModal = (typeKey = null, typeData = null) => {
    setSelectedEquipmentTypeKey(typeKey);
    setSelectedEquipmentType(typeData);
    setIsNewType(typeKey === null);
    onTypeModalOpen();
  };

  // フィールド編集モーダルを開く
  const openFieldModal = (typeKey, fieldName = null, fieldData = null) => {
    // 編集不可フィールドは編集できない
    if (fieldName && !isFieldEditable(fieldName)) {
      toast({
        title: '編集できません',
        description: 'このフィールドは編集・削除できません',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSelectedEquipmentTypeKey(typeKey);
    setSelectedFieldName(fieldName);
    setSelectedField(fieldData);
    setIsNewField(fieldName === null);
    onFieldModalOpen();
  };

  if (loading) {
    return (
      <Flex align="center" justify="center" minH="60vh">
        <Spinner size="xl" />
        <Text ml={4} fontSize="xl">設備定義を読み込み中...</Text>
      </Flex>
    );
  }

  const headerBg = useColorModeValue('gray.50', 'gray.800');
  const headerBorder = useColorModeValue('gray.200', 'gray.700');

  return (
    <Box minH="100vh" bg="gray.50">
      {/* 管理者用のヘッダー */}
      <Box as="header" bg={headerBg} borderBottomWidth="1px" borderColor={headerBorder} px={4} py={3} mb={4}>
        <Flex align="center" justify="space-between">
          <HStack spacing={4}>
            <IconButton
              icon={<ChevronLeftIcon />}
              aria-label="戻る"
              onClick={() => {
                if (hasChanges) {
                  if (window.confirm('未保存の変更があります。ページを離れますか？')) {
                    navigate('/admin/master-equipment');
                  }
                } else {
                  navigate('/admin/master-equipment');
                }
              }}
            />
            <Heading size="md">設備定義管理</Heading>
            {hasChanges && (
              <Badge colorScheme="yellow" fontSize="sm">
                未保存の変更あり
              </Badge>
            )}
          </HStack>
          <HStack spacing={2}>
            <Button
              bg="black"
              color="white"
              onClick={() => openTypeModal()}
              leftIcon={<AddIcon />}
            >
              設備タイプ追加
            </Button>

            {hasChanges && (
              <Button
                variant="outline"
                onClick={handleReset}
              >
                変更をリセット
              </Button>
            )}
            {hasChanges && (
              <Button
                colorScheme="red"
                onClick={handleSaveAll}
                isLoading={saving}
              >
                保存 *
              </Button>
            )}
          </HStack>
        </Flex>
      </Box>

      <Box px={4} pb={4}>
        {error && (
          <Alert status="error" mb={4}>
            <AlertIcon />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* 設備タイプ一覧 */}
        <Grid templateColumns="1fr" gap={6}>
          {Object.entries(definitions.equipment_types || {})
            .sort((a, b) => (a[1].display_order || 0) - (b[1].display_order || 0))
            .map(([typeKey, typeData], index, sortedArray) => (
            <Card key={typeKey} variant="outline">
              <CardHeader>
                <Flex justify="space-between" align="center">
                  <HStack>
                    <Text fontSize="xl">{typeData.icon}</Text>
                    <Box>
                      <Heading size="md">{typeData.name}</Heading>
                      <Text fontSize="sm" color="gray.500">({typeKey})</Text>
                    </Box>
                    {typeData.enabled ? (
                      <Badge colorScheme="blackAlpha">有効</Badge>
                    ) : (
                      <Badge colorScheme="red">無効</Badge>
                    )}
                    {typeData.enable_popularity !== false ? (
                      <Badge colorScheme="yellow" size="sm">人気順ON</Badge>
                    ) : (
                      <Badge colorScheme="gray" size="sm">人気順OFF</Badge>
                    )}
                    {typeData.enable_guide !== false ? (
                      <Badge colorScheme="yellow" size="sm">ガイドON</Badge>
                    ) : (
                      <Badge colorScheme="gray" size="sm">ガイドOFF</Badge>
                    )}
                  </HStack>
                  <HStack>
                    <VStack spacing={0}>
                      <IconButton
                        icon={<ChevronUpIcon />}
                        aria-label="上に移動"
                        size="xs"
                        onClick={() => moveEquipmentType(typeKey, 'up')}
                        isDisabled={index === 0}
                      />
                      <IconButton
                        icon={<ChevronDownIcon />}
                        aria-label="下に移動"
                        size="xs"
                        onClick={() => moveEquipmentType(typeKey, 'down')}
                        isDisabled={index === sortedArray.length - 1}
                      />
                    </VStack>
                    <IconButton
                      icon={<EditIcon />}
                      aria-label="設備タイプ編集"
                      size="sm"
                      colorScheme="blackAlpha"
                      onClick={() => openTypeModal(typeKey, typeData)}
                    />
                    <IconButton
                      icon={<DeleteIcon />}
                      aria-label="設備タイプ削除"
                      size="sm"
                      colorScheme="red"
                      onClick={() => handleDeleteEquipmentType(typeKey)}
                    />
                  </HStack>
                </Flex>
              </CardHeader>
              <CardBody>
                <VStack align="stretch" spacing={3}>
                  <Flex justify="space-between" align="center">
                    <Text fontWeight="bold">フィールド</Text>
                    <Button
                      size="sm"
                      colorScheme="blackAlpha"
                      leftIcon={<AddIcon />}
                      onClick={() => openFieldModal(typeKey)}
                    >
                      追加
                    </Button>
                  </Flex>
                  
                  {Object.entries(typeData.fields || {})
                    .sort((a, b) => (a[1].display_order || 0) - (b[1].display_order || 0))
                    .map(([fieldName, fieldData], fieldIndex, sortedFields) => (
                    <Card key={fieldName} variant="outline" size="sm">
                      <CardBody>
                        <Flex justify="space-between" align="center">
                          <VStack align="start" spacing={1}>
                            <HStack>
                              <Text fontWeight="bold">{fieldData.label}</Text>
                              <Text fontSize="sm" color="gray.500">({fieldName})</Text>
                              {fieldData.required && <Badge colorScheme="red" size="sm">必須</Badge>}
                              {fieldData.showInCard !== false && <Badge colorScheme="blackAlpha" size="sm">カード表示</Badge>}
                              {fieldData.showInCard === false && <Badge colorScheme="gray" size="sm">カード非表示</Badge>}
                              {!isFieldEditable(fieldName) && <Badge colorScheme="yellow" size="sm">編集不可</Badge>}
                            </HStack>
                            <HStack>
                              <Badge colorScheme="blackAlpha">{fieldData.type}</Badge>
                              {fieldData.unit && <Text fontSize="sm">単位: {fieldData.unit}</Text>}
                            </HStack>
                          </VStack>
                          <HStack>
                            <VStack spacing={0}>
                              <IconButton
                                icon={<ChevronUpIcon />}
                                aria-label="上に移動"
                                size="xs"
                                onClick={() => moveField(typeKey, fieldName, 'up')}
                                isDisabled={fieldIndex === 0}
                              />
                              <IconButton
                                icon={<ChevronDownIcon />}
                                aria-label="下に移動"
                                size="xs"
                                onClick={() => moveField(typeKey, fieldName, 'down')}
                                isDisabled={fieldIndex === sortedFields.length - 1}
                              />
                            </VStack>
                            {isFieldEditable(fieldName) && (
                              <>
                                <IconButton
                                  icon={<EditIcon />}
                                  aria-label="フィールド編集"
                                  size="sm"
                                  colorScheme="blackAlpha"
                                  onClick={() => openFieldModal(typeKey, fieldName, fieldData)}
                                />
                                <IconButton
                                  icon={<DeleteIcon />}
                                  aria-label="フィールド削除"
                                  size="sm"
                                  colorScheme="red"
                                  onClick={() => handleDeleteField(typeKey, fieldName)}
                                />
                              </>
                            )}
                          </HStack>
                        </Flex>
                      </CardBody>
                    </Card>
                  ))}
                  
                  {Object.keys(typeData.fields || {}).length === 0 && (
                    <Text color="gray.500" textAlign="center" py={4}>
                      フィールドがありません
                    </Text>
                  )}
                </VStack>
              </CardBody>
            </Card>
          ))}
        </Grid>

        {Object.keys(definitions.equipment_types || {}).length === 0 && (
          <Text color="gray.500" textAlign="center" py={8} fontSize="lg">
            設備タイプがありません。「設備タイプ追加」ボタンから追加してください。
          </Text>
        )}
      </Box>

      {/* モーダル */}
      <EquipmentTypeModal
        isOpen={isTypeModalOpen}
        onClose={onTypeModalClose}
        equipmentType={selectedEquipmentType}
        onSave={handleSaveEquipmentType}
        isNew={isNewType}
        existingTypeKeys={Object.keys(definitions.equipment_types || {})}
      />

      <FieldModal
        isOpen={isFieldModalOpen}
        onClose={onFieldModalClose}
        field={selectedField}
        fieldName={selectedFieldName}
        onSave={handleSaveField}
        isNew={isNewField}
      />
    </Box>
  );
}